import React from 'react';
import { Pressable, StyleSheet } from 'react-native';

import { CloseSmallIcon } from '@/assets/icons/CloseSmallIcon';
import ChevronRight from '@/assets/icons/chevronRightCasino';
import { ds } from '@/components/lib/design.system';
import { appTheme } from '@/components/lib/theme';
import { useHover } from '@/feature/responsive-design/hooks/use-hover';

const BUTTON_SIZE = 36;

type DesktopActionButtonProps = {
    variant: 'prev' | 'next' | 'close';
    onPress: () => void;
    disabled?: boolean;
    accessibilityLabel: string;
    testID?: string;
};

/**
 * Round prev/next/close button of desktop web carousels and panels.
 */
export const DesktopActionButton = ({
    variant,
    onPress,
    disabled = false,
    accessibilityLabel,
    testID,
}: DesktopActionButtonProps) => {
    const { hovered, hoverHandlers } = useHover();
    const iconColor = disabled ? ds.colorValue.icon.disabled : ds.colorValue.icon.secondary;

    return (
        <Pressable
            {...hoverHandlers}
            onPress={onPress}
            disabled={disabled}
            style={[styles.button, hovered && !disabled ? styles.buttonHovered : undefined]}
            accessibilityRole="button"
            accessibilityLabel={accessibilityLabel}
            accessibilityState={{ disabled }}
            testID={testID}
        >
            {variant === 'close' ? (
                <CloseSmallIcon color={iconColor} />
            ) : (
                <ChevronRight color={iconColor} style={variant === 'prev' ? styles.flipped : undefined} />
            )}
        </Pressable>
    );
};

const styles = StyleSheet.create({
    button: {
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: ds.colorValue.background.neutral.subtler,
        borderColor: ds.colorValue.border.strong,
        borderRadius: appTheme.borderRadii.r36,
        borderWidth: 1,
        height: BUTTON_SIZE,
        width: BUTTON_SIZE,
    },
    buttonHovered: {
        backgroundColor: ds.colorValue.background.neutral.subtle,
    },
    flipped: {
        transform: [{ rotate: '180deg' }],
    },
});
